import { useEffect, useState } from 'react';
import { Alert, Box } from '@mui/material';
import {
  CustomButton,
  DrawerForm,
  DrawerFormSidebar,
  TableBodySkeleton,
} from '../../../common/components';
import { toProviderOptions } from '../../utils/toProviderOptions';
import ConsolidatedSupplyMaterials from './ConsolidatedSupplyMaterials';
import ConsolidatedSupplyProviderFields from './ConsolidatedSupplyProviderFields';
import ConsolidatedSupplyExclusions from './ConsolidatedSupplyExclusions';
import ConsolidatedSupplySummary from './ConsolidatedSupplySummary';
import ConsolidatedSupplyFooter from './ConsolidatedSupplyFooter';
import {
  CONSOLIDATED_SUPPLY_SECTIONS,
  CONSOLIDATED_SUPPLY_TEXTS as TEXTS,
} from '../constants/consolidatedSupplyTexts';

/** Preparación de compras con el sidebar común y una vista por sección. */
const ConsolidatedSupplyDrawer = ({
  open,
  rows,
  excluded,
  providers,
  provider,
  commitment,
  selectedCount,
  onProvider,
  onCommitment,
  onQuantity,
  onClose,
  onSave,
  onRefresh,
  onView,
  isLoading,
  isSaving,
  error,
  created,
  canSave,
  documentCount,
  saveHint,
}) => {
  const [activeSection, setActiveSection] = useState('purchase');
  useEffect(() => {
    if (open) setActiveSection('purchase');
  }, [open]);
  useEffect(() => {
    if (activeSection === 'excluded' && excluded.length === 0) {
      setActiveSection('purchase');
    }
  }, [activeSection, excluded.length]);
  const chosen = rows.filter((row) => Number(row.quantity) > 0);
  const disabled = isLoading || isSaving || Boolean(created.length);
  const providerLabel = toProviderOptions(providers).find(
    (option) => option.value === provider
  )?.label;
  const sections = CONSOLIDATED_SUPPLY_SECTIONS.filter(
    (section) => section.id !== 'excluded' || excluded.length > 0
  ).map((section) =>
    section.id === 'excluded'
      ? { ...section, label: TEXTS.excludedCount(excluded.length) }
      : section
  );
  return (
    <DrawerForm
      open={open}
      onClose={onClose}
      title={TEXTS.title}
      width={{ xs: '100vw', md: 'min(1200px, calc(100vw - 40px))' }}
      sidebarWidth={210}
      sidebar={
        <DrawerFormSidebar
          title={TEXTS.sections}
          sections={sections}
          activeSection={activeSection}
          onSelect={setActiveSection}
        />
      }
      footer={
        <ConsolidatedSupplyFooter
          created={created}
          documentCount={documentCount}
          onClose={onClose}
          onView={onView}
          onSave={onSave}
          onRefresh={onRefresh}
          isSaving={isSaving}
          canSave={canSave}
          isLoading={isLoading}
          activeSection={activeSection}
          saveHint={saveHint}
          onReview={() => setActiveSection('summary')}
        />
      }
    >
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, minWidth: 0 }}
        aria-busy={isLoading}
      >
        {error && (
          <Box sx={{ mx: 2 }}>
            <Alert
              severity="error"
              action={
                <CustomButton
                  variant="outlined"
                  onClick={onRefresh}
                  disabled={isSaving || isLoading}
                >
                  {TEXTS.retry}
                </CustomButton>
              }
            >
              {error}
            </Alert>
          </Box>
        )}
        {isLoading ? (
          <TableBodySkeleton variant="box" rows={4} />
        ) : (
          <>
            {activeSection === 'purchase' && (
              <ConsolidatedSupplyProviderFields
                providers={providers}
                provider={provider}
                commitment={commitment}
                onProvider={onProvider}
                onCommitment={onCommitment}
                disabled={disabled}
                selectedCount={selectedCount}
                includedCount={new Set(rows.map((row) => row.uuid_external_order)).size}
                excludedCount={excluded.length}
                onReviewExcluded={() => setActiveSection('excluded')}
                onNext={() => setActiveSection('materials')}
              />
            )}
            {(activeSection === 'materials' || activeSection === 'origins') && (
              <ConsolidatedSupplyMaterials
                key={activeSection}
                rows={rows}
                onQuantity={onQuantity}
                showOrigins={activeSection === 'origins'}
                disabled={disabled}
                onAdjust={() => setActiveSection('origins')}
              />
            )}
            {activeSection === 'excluded' && <ConsolidatedSupplyExclusions rows={excluded} />}
            {activeSection === 'summary' && (
              <ConsolidatedSupplySummary
                rows={chosen}
                providerLabel={providerLabel}
                commitment={commitment}
                onEditPurchase={() => setActiveSection('purchase')}
              />
            )}
          </>
        )}
      </Box>
    </DrawerForm>
  );
};

export default ConsolidatedSupplyDrawer;
